import React, { useContext, useState, useEffect, useMemo } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { X, Plus, Minus, ShoppingBag, ShieldCheck, Zap, Cpu } from 'lucide-react';
import { AppContext } from '../context/AppContext';


const API_BASE_URL = process.env.REACT_APP_API_URL || 'https://api.swiftofficesupply.co.ke';
// const API_BASE_URL = process.env.REACT_APP_API_URL || 'http://localhost:5001';

const ProductModal = ({ product, onClose, openAuth }) => {
    const { addToCart, user } = useContext(AppContext);
    const [qty, setQty] = useState(1);
    const [imgLoaded, setImgLoaded] = useState(false);

    // OPTIMIZATION: Memoize image parsing
    const images = useMemo(() => {
        try {
            let data = product?.images;
            if (!data) return [];
            
            while (typeof data === 'string') {
                data = JSON.parse(data);
            }
            
            if (Array.isArray(data)) {
                return data.map(img => {
                    if (!img) return '';
                    if (img.startsWith('http')) return img;
                    const cleanPath = img.startsWith('/') ? img : `/${img}`;
                    return `${API_BASE_URL}${cleanPath}`;
                });
            }
            return [];
        } catch (e) { 
            return []; 
        }
    }, [product?.images]);

    const [activeImg, setActiveImg] = useState(images[0] || '');
    const [zoomPos, setZoomPos] = useState({ x: 0, y: 0, show: false });

    useEffect(() => {
        if (images.length > 0) {
            setActiveImg(images[0]);
        }
        setImgLoaded(false);
    }, [images]);

    const handleMouseMove = (e) => {
        const { left, top, width, height } = e.currentTarget.getBoundingClientRect();
        const x = ((e.pageX - left) / width) * 100;
        const y = ((e.pageY - top) / height) * 100;
        setZoomPos({ x, y, show: true });
    };

    const handleAddToCart = () => {
        if (!user) {
            if (typeof openAuth === 'function') {
                openAuth();
            } else {
                alert("Please log in to add items to your cart.");
            }
            return;
        }
        addToCart(product, qty);
    };

    return (
        <motion.div 
            initial={{ opacity: 0 }} 
            animate={{ opacity: 1 }} 
            exit={{ opacity: 0 }}
            className="fixed inset-0 z-[100] flex items-center justify-center backdrop-blur-2xl bg-black/70 p-3 md:p-6 overflow-hidden"
        >
            <motion.div 
                initial={{ scale: 0.95, opacity: 0 }} 
                animate={{ scale: 1, opacity: 1 }} 
                exit={{ scale: 0.95, opacity: 0 }}
                transition={{ type: "spring", damping: 25, stiffness: 300 }}
                className="relative w-full max-w-5xl max-h-[92vh] bg-white dark:bg-[#080808] rounded-[32px] md:rounded-[40px] shadow-2xl border border-white/10 flex flex-col overflow-hidden"
            >
                {/* Close Button */}
                <button 
                    onClick={onClose} 
                    className="absolute top-4 right-4 z-50 p-2.5 bg-gray-100 dark:bg-white/5 rounded-full hover:bg-red-600 hover:text-white transition-all shadow-md"
                >
                    <X size={18} />
                </button>

                <div className="grid lg:grid-cols-2 gap-4 md:gap-8 p-5 md:p-8 h-full items-center overflow-y-auto md:overflow-hidden scrollbar-hide">
                    
                    {/* Visual Showcase Section */}
                    <div className="flex flex-col justify-center">
                        <div 
                            className="relative w-full aspect-square max-h-[340px] md:max-h-[380px] rounded-[24px] md:rounded-[32px] bg-gray-50 dark:bg-white/5 border border-gray-100 dark:border-white/10 overflow-hidden cursor-zoom-in flex items-center justify-center mx-auto"
                            onMouseMove={handleMouseMove}
                            onMouseLeave={() => setZoomPos({ ...zoomPos, show: false })}
                        >
                            {!imgLoaded && (
                                <div className="absolute inset-0 z-10 bg-white/5 animate-pulse flex items-center justify-center">
                                    <div className="w-8 h-8 border-3 border-red-600/20 border-t-red-600 rounded-full animate-spin" />
                                </div>
                            )}

                            {activeImg ? (
                                <motion.img 
                                    key={activeImg}
                                    initial={{ opacity: 0 }} 
                                    animate={{ opacity: imgLoaded ? 1 : 0 }}
                                    transition={{ duration: 0.3 }}
                                    src={activeImg} 
                                    onLoad={() => setImgLoaded(true)}
                                    onError={() => setImgLoaded(true)}
                                    className="w-full h-full object-contain p-4 transition-transform duration-200"
                                    style={zoomPos.show ? { 
                                        transformOrigin: `${zoomPos.x}% ${zoomPos.y}%`, 
                                        transform: 'scale(2)' 
                                    } : {}}
                                    alt={product.model_name}
                                />
                            ) : (
                                <div className="text-gray-400 text-[10px] font-black uppercase tracking-widest">No Image</div>
                            )}
                        </div>

                        {/* Thumbnails */}
                        {images.length > 1 && (
                            <div className="flex gap-2 mt-4 overflow-x-auto pb-1 px-1 scrollbar-hide justify-center">
                                {images.map((img, idx) => (
                                    <button 
                                        key={idx} 
                                        onClick={() => { setActiveImg(img); setImgLoaded(false); }}
                                        className={`w-14 h-14 flex-shrink-0 rounded-xl p-1.5 bg-white dark:bg-white/5 border-2 transition-all ${activeImg === img ? 'border-red-600 scale-105 shadow-md' : 'border-transparent opacity-40 hover:opacity-80'}`}
                                    >
                                        <img src={img} className="w-full h-full object-contain" alt="" />
                                    </button>
                                ))}
                            </div>
                        )}
                    </div>

                    {/* Content Section */}
                    <div className="flex flex-col justify-between py-1">
                        <div>
                            <div className="flex flex-wrap gap-2 mb-2">
                                <span className={`flex items-center gap-1 px-3 py-1 rounded-full text-[8px] font-black uppercase tracking-widest border ${product.is_active ? 'bg-green-500/15 text-green-500 border-green-500/30' : 'bg-red-500/15 text-red-500 border-red-500/30'}`}>
                                    {product.is_active ? <ShieldCheck size={10}/> : <Zap size={10}/>}
                                    {product.is_active ? 'Ready for Dispatch' : 'Awaiting Stock'}
                                </span>
                                <span className="px-3 py-1 rounded-full bg-red-600 text-white text-[8px] font-black uppercase tracking-widest shadow-sm">
                                    {product.Brand?.name || 'SWIFT'}
                                </span>
                            </div>

                            <h1 className="text-2xl md:text-3xl font-black tracking-tighter text-slate-900 dark:text-white leading-tight mb-1">
                                {product.model_name}
                            </h1>
                            <p className="text-gray-400 font-mono text-[9px] uppercase mb-3 tracking-widest">Identifier: {product.sku}</p>

                            <div className="mb-4 p-3 bg-gray-50 dark:bg-white/5 rounded-[16px] border border-gray-100 dark:border-white/10">
                                <div className="flex items-center gap-1.5 mb-1 text-red-600">
                                    <Cpu size={14} />
                                    <span className="font-black text-[9px] uppercase tracking-widest">Spec Profile</span>
                                </div>
                                <p className="text-xs font-medium text-slate-700 dark:text-gray-300 leading-relaxed line-clamp-2">
                                    {product.short_desc || "Precision hardware for elite workflows."}
                                </p>
                            </div>
                        </div>

                        {/* Price & Action Pill */}
                        <div className="bg-slate-900 dark:bg-white p-2 rounded-[24px] shadow-xl my-2">
                            <div className="flex items-center justify-between gap-3 px-3 py-1">
                                <div>
                                    <span className="block text-[8px] font-bold text-gray-400 dark:text-gray-500 uppercase tracking-[2px]">Price</span>
                                    <div className="flex items-center gap-1">
                                        <span className="text-[10px] font-bold text-red-500">KES</span>
                                        <span className="text-2xl font-black text-white dark:text-black tracking-tight">
                                            {Number(product.price).toLocaleString()}
                                        </span>
                                    </div>
                                </div>

                                <div className="flex items-center gap-2">
                                    <div className="flex items-center gap-1 bg-white/5 dark:bg-black/5 px-2 py-1 rounded-full border border-white/10">
                                        <button onClick={() => setQty(q => Math.max(1, q-1))} className="text-red-500 p-1"><Minus size={14}/></button>
                                        <span className="text-white dark:text-black font-black text-sm w-4 text-center">{qty}</span>
                                        <button onClick={() => setQty(q => q+1)} className="text-green-500 p-1"><Plus size={14}/></button>
                                    </div>
                                    <button 
                                        onClick={handleAddToCart}
                                        className="bg-red-600 hover:bg-red-700 text-white px-5 py-2.5 rounded-full font-black text-[10px] uppercase tracking-[1.5px] transition-all flex items-center gap-1.5 shadow-md"
                                    >
                                        <ShoppingBag size={14} /> Add to Cart
                                    </button>
                                </div>
                            </div>
                        </div>

                        <div className="text-[11px] text-gray-500 dark:text-gray-400 leading-relaxed line-clamp-3">
                            {product.long_desc || "No detailed description provided for this unit."}
                        </div>
                    </div>
                </div>
            </motion.div>
        </motion.div>
    );
};

export default ProductModal;